import React, { useEffect, useState } from 'react';
import { Button, ButtonGroup, Icon, TextPassage, Table, Section, Searchbar, Toolbar, ToolbarItem } from '../common';
import SubscriptionRequest from '../../models/entities/SubscriptionRequest';
import './PolicyExemptionRequestSelectAssignment.css';
import { ProgressBar } from 'react-unity';
import SubscriptionRequestsService from '../../services/SubscriptionRequestsService';
import { ResourceGroupEntry } from './components/AddResourceGroupModal';
import { PolicyAssignmentResponse } from '../../models/viewModels/PolicyExemptionRequests/PolicyAssignmentResponse';

export interface PolicyExemptionAssignmentProps {
	selectedSubscriptions: SubscriptionRequest[];
	entriesBySubscription: Record<number, ResourceGroupEntry[]>;
	initialSelectedAssignmentBySubscription?: Record<number, PolicyAssignmentResponse>;
	cachedAssignmentsBySubscription?: Record<number, PolicyAssignmentResponse[]>;
	loadingAssignments?: boolean;
	onNext: (selectedAssignmentBySubscription: Record<number, PolicyAssignmentResponse>) => void;
	onBack: () => void;
}

const PolicyExemptionRequestSelectAssignment = ({
	selectedSubscriptions,
	entriesBySubscription,
	initialSelectedAssignmentBySubscription,
	cachedAssignmentsBySubscription,
	loadingAssignments: externalLoading,
	onNext,
	onBack,
}: PolicyExemptionAssignmentProps) => {
	const subscriptionRequestsService = new SubscriptionRequestsService();

	const [assignmentsBySubscription, setAssignmentsBySubscription] = useState<
		Record<number, PolicyAssignmentResponse[]>
	>(() => cachedAssignmentsBySubscription ?? {});
	const [selectedAssignmentBySubscription, setSelectedAssignmentBySubscription] = useState<
		Record<number, PolicyAssignmentResponse>
	>(() => initialSelectedAssignmentBySubscription ?? {});
	const [localLoading, setLocalLoading] = useState(false);
	const loadingAssignments = externalLoading || localLoading;
	const [searchBySubscription, setSearchBySubscription] = useState<Record<number, string>>({});

	const handleBack = () => {
		onBack();
	};

	const handleNext = () => {
		onNext(selectedAssignmentBySubscription);
	};

	// Sync any new entries fetched by Home while this step was not mounted
	useEffect(() => {
		if (cachedAssignmentsBySubscription) {
			setAssignmentsBySubscription((prev) => ({ ...cachedAssignmentsBySubscription, ...prev }));
		}
	}, [cachedAssignmentsBySubscription]);

	const handleAssignmentSelect = (subscriptionId: number, assignment: PolicyAssignmentResponse) => {
		setSelectedAssignmentBySubscription((prev) => ({
			...prev,
			[subscriptionId]: assignment,
		}));
	};

	return (
		<>
			<TextPassage>
				<p>
					Select a policy assignment for each subscription group. Each group can have a different assignment.
				</p>
			</TextPassage>
			{selectedSubscriptions.length === 0 ? (
				<TextPassage>
					<p>No subscriptions selected. Please go back and select at least one subscription.</p>
				</TextPassage>
			) : loadingAssignments ? (
				<ProgressBar indeterminate hideValueLabel label="Loading policy assignments..." />
			) : (
				<>
					{selectedSubscriptions.map((sub, index) => (
						<Section
							key={sub.id}
							title={
								sub.createdSubscriptionName ?? sub.createdSubscriptionId ?? `Subscription ${index + 1}`
							}
							type="em-c-section--expandable"
							isInError={!selectedAssignmentBySubscription[sub.id]}
							titleFontSize="large"
							marginNone
						>
							<Table
								header={
									<Toolbar>
										<ToolbarItem width="lg">
											<Searchbar
												condensed
												value={searchBySubscription[sub.id] ?? ''}
												onChange={(e) =>
													setSearchBySubscription((prev) => ({
														...prev,
														[sub.id]: e.target.value,
													}))
												}
												onClick={() =>
													setSearchBySubscription((prev) => ({ ...prev, [sub.id]: '' }))
												}
												placeholder="Search assignments…"
											/>
										</ToolbarItem>
									</Toolbar>
								}
							>
								<Table.Head>
									<Table.Head.Row>
										<Table.Head.Cell />
										<Table.Head.Cell> Assignment </Table.Head.Cell>
										<Table.Head.Cell> Description </Table.Head.Cell>
										<Table.Head.Cell> Scope </Table.Head.Cell>
										<Table.Head.Cell> Policies </Table.Head.Cell>
									</Table.Head.Row>
								</Table.Head>
								<Table.Body>
									{(assignmentsBySubscription[sub.id] ?? [])
										.filter((assignment) => {
											const q = (searchBySubscription[sub.id] ?? '').toLowerCase().trim();
											if (!q) return true;
											return (
												assignment?.properties.displayName?.toLowerCase().includes(q) ||
												assignment?.properties.description?.toLowerCase().includes(q) ||
												assignment?.properties.scopeDisplayName?.toLowerCase().includes(q)
											);
										})
										.map((assignment) => {
											const isSelected =
												selectedAssignmentBySubscription[sub.id]?.id === assignment.id;

											return (
												<Table.Row
													key={assignment.id}
													onClick={() => handleAssignmentSelect(sub.id, assignment)}
												>
													<Table.Cell>
														<input
															type="radio"
															name={`assignment-${sub.id}`}
															checked={isSelected}
															onChange={() => handleAssignmentSelect(sub.id, assignment)}
														/>
													</Table.Cell>
													<Table.Cell> {assignment?.properties.displayName} </Table.Cell>
													<Table.Cell>{assignment?.properties.description}</Table.Cell>
													<Table.Cell>{assignment?.properties.scopeDisplayName}</Table.Cell>
													<Table.Cell>
														{assignment?.properties.policyDefinitions?.length}
													</Table.Cell>
												</Table.Row>
											);
										})}
								</Table.Body>
							</Table>
						</Section>
					))}
				</>
			)}
			<br />
			<ButtonGroup>
				<Button variant="secondary" size="small" onClick={handleBack}>
					<Icon icon="chevron-left" size="small" />
					<span>Back</span>
				</Button>
				<Button
					variant="primary"
					size="small"
					disabled={Object.keys(selectedAssignmentBySubscription).length !== selectedSubscriptions.length}
					onClick={handleNext}
				>
					<span>Next Step</span>
					<Icon icon="chevron-right" size="small" />
				</Button>
			</ButtonGroup>
		</>
	);
};

export default PolicyExemptionRequestSelectAssignment;
